import { useEffect } from 'react';
import FloatingCta from '@/components/feature/FloatingCta';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import SeoHead from '@/components/feature/SeoHead';
import IconHeader from './components/IconHeader';
import IconHero from './components/IconHero';
import IconBeforeAfter from './components/IconBeforeAfter';
import IconConcerns from './components/IconConcerns';
import IconAbout from './components/IconAbout';
import IconReasons from './components/IconReasons';
import IconComparison from './components/IconComparison';
import IconMechanism from './components/IconMechanism';
import IconFlow from './components/IconFlow';
import IconPricing from './components/IconPricing';
import IconCases from './components/IconCases';
import IconDoctor from './components/IconDoctor';
import IconFaq from './components/IconFaq';
import IconAccess from './components/IconAccess';
import IconCta from './components/IconCta';
import IconPageFooter from './components/IconPageFooter';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const iconJsonLd = [
  {
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/icon/`,
    url: `${siteUrl}/icon/`,
    name: '歯の白い点、削らずに消せます。|ICON白斑治療|札幌 moyuk SAPPORO さいわいデンタルクリニック',
    description:
      '矯正後のホワイトスポット・歯の白い点をICONで改善。削らない・麻酔不要・1回完結。33,000円〜。無料カウンセリング受付中。',
    inLanguage: 'ja',
    about: {
      '@type': 'MedicalProcedure',
      name: 'ICON白斑治療',
      procedureType: 'http://schema.org/NoninvasiveProcedure',
      bodyLocation: '歯',
    },
    isPartOf: { '@id': `${siteUrl}/#website` },
  },
  {
    '@type': 'Service',
    name: 'ICON白斑治療',
    provider: {
      '@type': 'Dentist',
      name: 'moyuk SAPPORO さいわいデンタルクリニック',
      '@id': `${siteUrl}/#dentist`,
    },
    serviceType: 'ICON白斑治療',
    areaServed: { '@type': 'City', name: '札幌市' },
    offers: {
      '@type': 'Offer',
      price: '33000',
      priceCurrency: 'JPY',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '33000',
        priceCurrency: 'JPY',
        unitText: '1歯あたり',
      },
    },
    description:
      'ホワイトスポット・白斑を削らずに改善するICON治療。麻酔不要・1回完結・33,000円〜。',
  },
  {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'ICON白斑治療',
        item: `${siteUrl}/icon/`,
      },
    ],
  },
  {
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '痛みはありますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '治療中の痛みはほとんどありません。ジェルが歯茎に触れると軽いしみる感覚を覚えることがありますが、麻酔は不要です。',
        },
      },
      {
        '@type': 'Question',
        name: '完全に白い点がなくなりますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '白斑の深さや範囲によって異なります。多くの方で大幅に改善しますが、完全に消えない場合もあります。事前のカウンセリングで改善度の目安をご説明します。',
        },
      },
      {
        '@type': 'Question',
        name: '効果はどのくらい続きますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '適切なホームケアを続けていただければ、長期的に安定した状態を保てます。時間経過による色の変化は非常に小さいとされています。',
        },
      },
      {
        '@type': 'Question',
        name: '治療後に食事や生活の制限はありますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '特別な制限はありません。当日から通常通りの食事が可能です。長期的な状態を保つために、定期的なクリーニングをおすすめしています。',
        },
      },
      {
        '@type': 'Question',
        name: '矯正治療中でも受けられますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '矯正装置が装着中の場合は対応が難しいケースがあります。装置を外した後のタイミングでのご相談をおすすめします。まずはカウンセリングでご確認ください。',
        },
      },
    ],
  },
  {
    '@type': 'ImageGallery',
    '@id': `${siteUrl}/icon/#image-gallery`,
    name: 'ICON白斑治療 ビフォーアフター症例写真ギャラリー｜さいわいデンタルクリニック札幌',
    description:
      'さいわいデンタルクリニック moyuk SAPPORO（札幌）におけるICON白斑治療のビフォーアフター症例写真。先天性ホワイトスポット・矯正後の白斑・軽度・中等度症例を掲載。削らない・麻酔不要の治療で歯の白い点を改善。',
    url: `${siteUrl}/icon/`,
    author: {
      '@type': 'Dentist',
      '@id': `${siteUrl}/#dentist`,
      name: 'さいわいデンタルクリニック moyuk SAPPORO',
    },
    associatedMedia: [
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-main-before`,
        name: 'ホワイトスポット・白斑 ICON治療前 ― 前歯に白い点が見られる状態',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/b119c5cc47f4450f9ed4c4db51afaf39.jpeg',
        url: `${siteUrl}/icon/`,
        description:
          '前歯にホワイトスポット（白斑）が見られる治療前の状態。ICON治療で改善可能なケースの一例。削らない・麻酔不要の治療で自然な歯面を取り戻せます。さいわいデンタルクリニック moyuk SAPPORO（札幌市中央区）',
        caption: 'ICON白斑治療 ビフォー ― 治療前の歯のホワイトスポット（白い点）',
        encodingFormat: 'image/jpeg',
        keywords: 'ホワイトスポット,白斑,歯の白い点,治療前,ICON,札幌歯科',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
        representativeOfPage: true,
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-main-after`,
        name: 'ホワイトスポット・白斑 ICON治療後 ― 自然な透明感に改善',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/63a480ed3842d5cd03e9373db0882361.jpeg',
        url: `${siteUrl}/icon/`,
        description:
          'ICON治療後、白斑が改善され自然な透明感を取り戻した歯。削らない・麻酔不要・1回完結の治療で実現した症例。さいわいデンタルクリニック moyuk SAPPORO（札幌市中央区）',
        caption: 'ICON白斑治療 アフター ― 治療後の自然な歯の見た目',
        encodingFormat: 'image/jpeg',
        keywords: 'ホワイトスポット改善,白斑治療後,歯の白い点 治療,ICON治療結果,札幌歯科',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-congenital-before`,
        name: '先天性ホワイトスポット 治療前 ― 生まれつきの白斑症例',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/38ae7d1d391a4fe31c51218273532600.jpeg',
        url: `${siteUrl}/icon/`,
        description:
          '生まれつきの先天性ホワイトスポット（白斑）の治療前写真。ICON治療で削らずに自然な色合いへ近づけられる可能性があります。さいわいデンタルクリニック moyuk SAPPORO（札幌）',
        caption: '先天性ホワイトスポット 治療前 ― ICON白斑治療症例',
        encodingFormat: 'image/jpeg',
        keywords: '先天性ホワイトスポット,生まれつき白斑,歯の白い点 生まれつき,ICON治療,札幌',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-congenital-after`,
        name: '先天性ホワイトスポット ICON治療後 ― 自然な色合いに改善',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/332868c4c1fc8a17b01efb526b461c6b.jpeg',
        url: `${siteUrl}/icon/`,
        description:
          '先天性ホワイトスポット（白斑）のICON治療後。削らずに自然な色合いに近づけた症例。さいわいデンタルクリニック moyuk SAPPORO（札幌）',
        caption: '先天性ホワイトスポット ICON治療後 ― 自然な歯の色に改善',
        encodingFormat: 'image/jpeg',
        keywords: '先天性ホワイトスポット 治療後,白斑改善,ICON治療効果,札幌歯科',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-ortho-before`,
        name: '矯正後の白斑 治療前 ― ブラケット跡のホワイトスポット',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/d0c6d47c884d83ece0cbb6e3a123d706.png',
        url: `${siteUrl}/icon/`,
        description:
          '矯正装置（ブラケット）除去後に残った白斑の治療前写真。矯正後のホワイトスポットにもICON治療が有効です。さいわいデンタルクリニック moyuk SAPPORO（札幌）',
        caption: '矯正後ホワイトスポット 治療前 ― ブラケット跡の白い点',
        encodingFormat: 'image/png',
        keywords: '矯正後 白斑,ブラケット跡 ホワイトスポット,矯正 歯の白い点,ICON治療,札幌',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-ortho-after`,
        name: '矯正後の白斑 ICON治療後 ― 自然な歯面に改善',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/27357b3e93f8e88dbebb60b4c75319d7.png',
        url: `${siteUrl}/icon/`,
        description:
          '矯正後の白斑をICON治療で改善した症例。自然な歯面を取り戻し、笑顔に自信が持てるようになった症例。さいわいデンタルクリニック moyuk SAPPORO（札幌）',
        caption: '矯正後ホワイトスポット ICON治療後 ― 自然な歯に改善',
        encodingFormat: 'image/png',
        keywords: '矯正後 白斑改善,ブラケット跡 治療後,ホワイトスポット 矯正,ICON効果,札幌',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-mild-before`,
        name: '軽度ホワイトスポット 治療前 ― 初期虫歯・脱灰による白斑',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/82c52c700c0ab6003f388e18da9d9d69.jpeg',
        url: `${siteUrl}/icon/`,
        description:
          '虫歯初期・脱灰による軽度の白斑治療前写真。早期のホワイトスポットほど改善効果が高いケースが多いです。さいわいデンタルクリニック moyuk SAPPORO（札幌）',
        caption: '軽度ホワイトスポット 治療前 ― 初期脱灰による白い点',
        encodingFormat: 'image/jpeg',
        keywords: '脱灰 ホワイトスポット,初期虫歯 白い点,軽度白斑,ICON治療,札幌歯科',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-mild-after`,
        name: '軽度ホワイトスポット ICON治療後 ― 自然な光沢に改善',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/b4306c844d8827c6a82a81acdcb7db0a.jpeg',
        url: `${siteUrl}/icon/`,
        description:
          '軽度の白斑をICON治療で改善した症例。自然な光沢と滑らかな歯面を取り戻しています。さいわいデンタルクリニック moyuk SAPPORO（札幌）',
        caption: '軽度ホワイトスポット ICON治療後 ― 自然な光沢に改善',
        encodingFormat: 'image/jpeg',
        keywords: '脱灰 白斑改善,軽度ホワイトスポット 治療後,ICON効果,歯 白い点 消えた,札幌',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-moderate-before`,
        name: '中等度ホワイトスポット 治療前 ― 広範囲の白斑症例',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/7644c17f83e2488f6e9f3e0cfeb63cfe.jpeg',
        url: `${siteUrl}/icon/`,
        description:
          '範囲が広くやや濃い中等度の白斑の治療前写真。このような中等度症例もICON治療での改善が期待できます。さいわいデンタルクリニック moyuk SAPPORO（札幌）',
        caption: '中等度ホワイトスポット 治療前 ― 広範囲の白い点',
        encodingFormat: 'image/jpeg',
        keywords: '中等度 ホワイトスポット,広範囲 白斑,歯の白い点 範囲広い,ICON治療,札幌',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
      {
        '@type': 'ImageObject',
        '@id': `${siteUrl}/icon/#img-moderate-after`,
        name: '中等度ホワイトスポット ICON治療後 ― 白斑が大幅に改善',
        contentUrl:
          'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/64560b534df1c2cc5ca7f8d95a21a47e.jpeg',
        url: `${siteUrl}/icon/`,
        description:
          '中等度の白斑をICON治療で改善した症例。白い点が大幅に目立たなくなり自然な透明感が戻りました。さいわいデンタルクリニック moyuk SAPPORO（札幌）',
        caption: '中等度ホワイトスポット ICON治療後 ― 大幅改善した症例',
        encodingFormat: 'image/jpeg',
        keywords: '中等度白斑 治療後,広範囲ホワイトスポット 改善,ICON効果,歯 白い点 消えた,札幌',
        about: { '@type': 'MedicalProcedure', name: 'ICON白斑治療' },
      },
    ],
  },
];

export default function IconPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div id="top" className="min-h-screen bg-white">
      <SeoHead
        title="歯の白い点、削らずに消せます。|ICON白斑治療 札幌 | moyuk SAPPORO さいわいデンタル"
        description="矯正後のホワイトスポット・歯の白い点をICONで改善。削らない・麻酔不要・1回完結。33,000円〜。無料カウンセリング受付中。札幌・moyuk SAPPORO 2F。"
        keywords="ICON白斑治療,ホワイトスポット,歯の白い点,札幌歯科,矯正後白斑,さいわいデンタル"
        canonical={`${siteUrl}/icon/`}
        ogType="website"
        ogTitle="歯の白い点、削らずに消せます。|ICON白斑治療 札幌 | moyuk SAPPORO"
        ogDescription="矯正後のホワイトスポット・歯の白い点をICONで改善。削らない・麻酔不要・1回完結。33,000円〜。無料カウンセリング受付中。"
        ogSiteName="moyuk SAPPORO さいわいデンタル"
        ogLocale="ja_JP"
        ogImageWidth="1200"
        ogImageHeight="630"
        ogImageAlt="ICON白斑治療・ホワイトスポット改善 札幌 moyuk SAPPORO さいわいデンタルクリニック"
        twitterCard="summary_large_image"
        twitterTitle="歯の白い点、削らずに消せます。|ICON白斑治療 札幌 | moyuk SAPPORO"
        twitterDescription="削らない・麻酔不要・1回完結。33,000円〜。札幌チカホ直結のmoyuk SAPPOROにあるICON白斑治療。"
        jsonLd={iconJsonLd}
      />
      <IconHeader />
      <main className="pb-16 md:pb-0">
        <IconHero />
        <IconBeforeAfter />
        <IconConcerns />
        <IconAbout />
        <IconReasons />
        <IconComparison />
        <IconMechanism />
        <section id="steps">
          <IconFlow />
        </section>
        <section id="pricing">
          <IconPricing />
        </section>
        <IconCases />
        <section id="doctor">
          <IconDoctor />
        </section>
        <section id="faq">
          <IconFaq />
        </section>
        <IconAccess />
        <IconCta />
      </main>
      <IconPageFooter />
      <FloatingCta
        href="https://reservation.stransa.co.jp/8878b04a60c23f1ce5b3268e265263cb"
        label="無料診断を予約する"
      />
      <MobileBottomNav />
    </div>
  );
}